import { ArrowDownRight, Star, MapPin } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { photos, business } from "@/data/siteData";

export default function Hero() {
  const ref = useReveal();
  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[88vh] md:min-h-[92vh] flex items-end overflow-hidden text-[#F4F0EA]"
    >
      {/* Background image */}
      <img
        data-testid="hero-image"
        src={photos.interior}
        alt="Inside Cafe Anh — Vietnamese coffee shop on Hämeentie, Helsinki"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
      />
      {/* Warm dark green overlay for legibility */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(19,42,28,0.55) 0%, rgba(19,42,28,0.35) 35%, rgba(19,42,28,0.78) 100%)",
        }}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-14 md:pb-20 pt-32">
        <div className="max-w-3xl">
          <p className="reveal text-[11px] tracking-[0.32em] uppercase text-[#D4A373] mb-5">
            Cafe Anh · {business.neighbourhood}, Helsinki
          </p>
          <h1 className="reveal font-serif-display text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            Authentic Vietnamese
            <br />
            <span className="italic text-[#D4A373]">coffee</span> in Helsinki.
          </h1>
          <p className="reveal mt-6 max-w-xl text-[#F4F0EA]/90 text-base md:text-lg leading-relaxed">
            A small, family-owned café on Hämeentie. Vietnamese iced coffee, bạc xỉu,
            egg coffee, fresh phở and banh mi — warm flavours, friendly atmosphere.
          </p>

          <div className="reveal mt-8 flex flex-wrap items-center gap-3">
            <a
              href="#menu"
              data-testid="hero-cta-menu"
              className="group inline-flex items-center gap-3 px-6 py-3.5 bg-[#F4F0EA] text-[#132A1C] text-[12px] tracking-[0.22em] uppercase hover:bg-[#D4A373] transition-colors"
            >
              See the menu
              <ArrowDownRight
                size={15}
                strokeWidth={1.5}
                className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1"
              />
            </a>
            <a
              href="#visit"
              data-testid="hero-cta-visit"
              className="inline-flex items-center gap-2 px-6 py-3.5 border border-[#F4F0EA]/50 text-[12px] tracking-[0.22em] uppercase text-[#F4F0EA] hover:bg-[#F4F0EA] hover:text-[#132A1C] transition-colors"
            >
              <MapPin size={14} strokeWidth={1.5} /> Visit us
            </a>
          </div>

          <div className="reveal mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[#F4F0EA]/90">
            <div className="flex items-center gap-2" data-testid="hero-rating">
              <div className="flex gap-0.5" aria-hidden>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    size={15}
                    strokeWidth={1.5}
                    className="fill-[#D4A373] text-[#D4A373]"
                  />
                ))}
              </div>
              <span className="font-serif-display text-lg">{business.rating}</span>
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#F4F0EA]/70">
                · {business.reviewsCount} Google reviews
              </span>
            </div>
            <span className="hidden sm:block w-px h-5 bg-[#F4F0EA]/30" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#F4F0EA]/70">
              {business.services}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
